import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, AlertCircle, Database,
  Building2, MapPin, Phone, Mail, FileText, CreditCard, Target,
  Calendar, Trophy, Clock, Truck, User, Briefcase, Ticket,
  ShoppingCart, Package, ChevronDown, ChevronUp, ShieldCheck,
} from 'lucide-react';
import { adminClientService, type AnagraficaDetail } from '../../services/adminClientService';
import { cartService } from '../../services/cartService';
import type { CartItem } from '../../types';
import { getCartItemUnitPrice } from '../../utils/cartPrice';
import { ChangeEmailCard, ResetCredentialsCard, CreateAccountCard } from '../../components/admin/ClientAccountCards';

const fmtEUR = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const fmtBool = (b: boolean | null | undefined) => b == null ? '—' : (b ? 'Sì' : 'No');

// ─────────────────────────────────────────────────────────────────────────
//   Sotto-componenti
// ─────────────────────────────────────────────────────────────────────────

function Field({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const display = value == null || value === '' ? <span className="text-muted-foreground">—</span> : value;
  return (
    <div data-testid={`detail-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''} mt-0.5 break-words`}>{display}</div>
    </div>
  );
}

function Section({
  icon, title, children, testid,
}: { icon: React.ReactNode; title: string; children: React.ReactNode; testid: string }) {
  return (
    <section className="rounded-xl border bg-white p-5" data-testid={testid}>
      <h2 className="text-sm font-semibold flex items-center gap-2 mb-4 pb-3 border-b">
        {icon}
        {title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
        {children}
      </div>
    </section>
  );
}

function BudgetMiniCard({ d }: { d: AnagraficaDetail }) {
  if (!d.budgetEnabled) {
    return (
      <div className="rounded-lg border bg-gray-50 p-3 text-xs text-muted-foreground" data-testid="budget-mini-disabled">
        Budget premium NON gestito per questo cliente.
      </div>
    );
  }
  const valore = d.budgetValore ?? 0;
  const fatturato = d.budgetFatturatoCorrente ?? 0;
  const pct = valore > 0 ? Math.min(100, (fatturato / valore) * 100) : 0;
  const isReached = d.budgetStatus === 'target-reached';
  const isExpired = d.budgetStatus === 'expired';

  const color = isReached
    ? 'border-emerald-200 bg-emerald-50/50'
    : isExpired
    ? 'border-gray-200 bg-gray-50'
    : 'border-blue-200 bg-blue-50/50';
  const barColor = isReached ? 'bg-emerald-500' : isExpired ? 'bg-gray-400' : 'bg-[hsl(214,58%,41%)]';
  const icon = isReached ? <Trophy className="w-4 h-4 text-emerald-600" />
    : isExpired ? <Clock className="w-4 h-4 text-gray-500" />
    : <Target className="w-4 h-4 text-[hsl(214,58%,41%)]" />;

  return (
    <div className={`rounded-lg border-2 p-4 ${color}`} data-testid="budget-mini-card">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {icon}
        <span className="text-sm font-semibold">
          {isReached ? `Sconto ${d.budgetPercentuale}% attivo`
            : isExpired ? 'Periodo terminato'
            : `Sblocco a ${fmtEUR(valore)}`}
        </span>
        <span className="text-[10px] uppercase tracking-wider bg-white/70 border rounded px-2 py-0.5 font-semibold" data-testid="budget-premio-badge">
          Premio budget · {d.budgetPercentuale}%
        </span>
      </div>
      <div className="text-xs text-muted-foreground mb-1.5 tabular-nums">
        Fatturato: <span className="font-semibold text-foreground" data-testid="budget-mini-fatturato">{fmtEUR(fatturato)}</span> / {fmtEUR(valore)}
        <span className="ml-1">({pct.toFixed(0)}%)</span>
      </div>
      <div className="h-2 w-full rounded-full bg-white/60 overflow-hidden">
        <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} data-testid="budget-mini-progress" />
      </div>
      {d.budgetDataInizio && (
        <div className="mt-2 text-[10px] text-muted-foreground space-y-0.5">
          <div>Inizio periodo: {fmtDate(d.budgetDataInizio)}</div>
          {d.budgetDataFine && <div>Fine periodo: {fmtDate(d.budgetDataFine)}</div>}
        </div>
      )}

      {/* Sezione Coupon (placeholder — coupon feature ancora da integrare) */}
      <div className="mt-3 pt-3 border-t border-current/10">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5 flex items-center gap-1">
          <Ticket className="w-3 h-3" /> Coupon premium
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
          <div>
            <span className="text-muted-foreground">Coupon disponibile:</span>{' '}
            <span className="font-mono font-semibold" data-testid="budget-coupon-code">
              {d.budgetCoupon || <span className="text-muted-foreground">—</span>}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Valore coupon:</span>{' '}
            <span className="tabular-nums font-semibold" data-testid="budget-coupon-valore">
              {d.budgetValoreCoupon != null && d.budgetValoreCoupon > 0
                ? fmtEUR(d.budgetValoreCoupon)
                : <span className="text-muted-foreground">—</span>}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Ultimo invio:</span>{' '}
            <span data-testid="budget-coupon-data-ultimo">
              {d.budgetDataUltimoCoupon
                ? fmtDate(d.budgetDataUltimoCoupon)
                : <span className="text-muted-foreground">—</span>}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Utilizzati:</span>{' '}
            <span className="tabular-nums font-semibold" data-testid="budget-coupon-utilizzati">
              {d.budgetCouponUtilizzati != null
                ? d.budgetCouponUtilizzati
                : <span className="text-muted-foreground">—</span>}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//   Carrello cliente (admin view)
// ─────────────────────────────────────────────────────────────────────────

function AdminCartSection({ anConto }: { anConto: number }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    cartService.adminGetCart(anConto)
      .then(res => {
        setItems((res.items ?? []) as CartItem[]);
        setUpdatedAt(res.updated_at ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [anConto]);

  const subtotale = items.reduce((sum, i) => sum + getCartItemUnitPrice(i) * i.qty, 0);
  const totalItems = items.reduce((sum, i) => sum + i.qty, 0);

  return (
    <section className="rounded-xl border bg-white p-5" data-testid="section-carrello-admin">
      <button
        className="w-full flex items-center justify-between text-sm font-semibold pb-0"
        onClick={() => setExpanded(v => !v)}
        data-testid="admin-cart-toggle"
      >
        <span className="flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-[hsl(214,58%,41%)]" />
          Carrello attivo
          {!loading && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${items.length > 0 ? 'bg-[hsl(214,58%,41%)] text-white' : 'bg-secondary text-muted-foreground'}`}>
              {totalItems} {totalItems === 1 ? 'articolo' : 'articoli'}
            </span>
          )}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t">
          {loading ? (
            <div className="animate-pulse space-y-2">
              <div className="h-8 bg-secondary rounded" />
              <div className="h-8 bg-secondary rounded w-3/4" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center py-6 text-center text-muted-foreground" data-testid="admin-cart-empty">
              <Package className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-sm">Carrello vuoto</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-cart-items">
              {items.map(item => {
                const unitPrice = getCartItemUnitPrice(item);
                return (
                  <div key={item.sku} className="flex items-start justify-between gap-3 rounded-lg border p-3 bg-secondary/20" data-testid={`admin-cart-item-${item.sku}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-mono text-[hsl(214,58%,41%)]">{item.sku}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-white/70">{item.brand}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold tabular-nums">
                        {item.qty} pz
                      </div>
                      {unitPrice > 0 && (
                        <div className="text-xs text-muted-foreground tabular-nums">
                          € {(unitPrice * item.qty).toFixed(2).replace('.', ',')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-xs text-muted-foreground">
                  {updatedAt ? `Aggiornato: ${new Date(updatedAt).toLocaleString('it-IT')}` : ''}
                </span>
                {subtotale > 0 && (
                  <span className="text-sm font-bold tabular-nums" data-testid="admin-cart-subtotale">
                    Totale: € {subtotale.toFixed(2).replace('.', ',')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//   Pagina principale
// ─────────────────────────────────────────────────────────────────────────

function ErpClientDetail({ anConto }: { anConto: number }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AnagraficaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (force = false) => {
    try {
      setError(null);
      const data = await adminClientService.getDetail(anConto, force);
      setDetail(data);
      if (force) toast.success('Cliente aggiornato dal gestionale: anagrafica, destinazioni, condizioni e ordini.');
    } catch (err: any) {
      const code = err?.response?.status;
      setError(
        code === 404 ? 'Anagrafica non trovata.'
        : code === 403 ? 'Accesso negato — ruolo admin/agente richiesto.'
        : code === 502 ? 'Il gestionale non ha risposto.'
        : 'Errore di caricamento.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!Number.isFinite(anConto)) {
      setError('Codice an_conto non valido.');
      setLoading(false);
      return;
    }
    setLoading(true);
    void load(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anConto]);

  if (loading) {
    return (
      <div className="p-6" data-testid="admin-client-detail-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary rounded w-64" />
          <div className="h-32 bg-secondary rounded" />
          <div className="h-48 bg-secondary rounded" />
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="p-6" data-testid="admin-client-detail-error">
        <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:underline mb-4 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Indietro
        </button>
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700">{error || 'Errore sconosciuto'}</p>
        </div>
      </div>
    );
  }

  const d = detail;
  return (
    <div className="p-6 space-y-5" data-testid="admin-client-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/admin/clienti" className="text-xs text-muted-foreground hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Clienti
            </Link>
            {(d.seed || d.budgetSeed) && (
              <span
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 font-semibold ml-1"
                title={d.seedLabel || 'Cliente di test'}
                data-testid="detail-seed-badge"
              >
                SEED · {d.seedLabel || 'fixture test'}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" data-testid="detail-ragione-sociale">
            <Building2 className="w-6 h-6 text-[hsl(206,54%,20%)]" />
            {d.ragioneSociale}
            {d.ragioneSocialeAlt && <span className="text-base font-normal text-muted-foreground">{d.ragioneSocialeAlt}</span>}
          </h1>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
            <span className="font-mono" data-testid="detail-an-conto">an_conto: <strong>{d.an_conto}</strong></span>
            {d.siglaRicerca && <span className="font-mono">Sigla: <strong>{d.siglaRicerca}</strong></span>}
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${d.status === 'bloccato' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {d.status}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <button
            onClick={() => { setRefreshing(true); void load(true); }}
            disabled={refreshing}
            data-testid="detail-refresh-btn"
            className="h-9 px-3 rounded-lg border text-xs font-medium flex items-center gap-1.5 hover:bg-secondary/60 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna dal gestionale
          </button>
          <div className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid="detail-source-indicator">
            <Database className="w-3 h-3 text-blue-500" />
            <span>Letto dal gestionale{d.fetchedAt ? ` il ${new Date(d.fetchedAt).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}</span>
          </div>
        </div>
      </div>

      {/* Budget */}
      <Section icon={<Target className="w-4 h-4 text-[hsl(214,58%,41%)]" />} title="Budget Premium" testid="section-budget">
        <div className="col-span-full">
          <BudgetMiniCard d={d} />
        </div>
      </Section>

      {/* Carrello attivo */}
      <AdminCartSection anConto={anConto} />

      {/* Anagrafica fiscale */}
      <Section icon={<FileText className="w-4 h-4" />} title="Dati fiscali" testid="section-fiscale">
        <Field label="Codice cliente" value={String(d.an_conto)} mono />
        <Field label="Partita IVA" value={d.partitaIva} mono />
        <Field label="Codice Fiscale" value={d.codiceFiscale} mono />
        <Field label="PEC" value={d.pec && (<a href={`mailto:${d.pec}`} className="text-[hsl(214,58%,41%)] hover:underline">{d.pec}</a>)} />
        <Field label="Codice SDI" value={d.codiceSDI} mono />
        <Field label="Email SDI" value={d.emailSdi && (<a href={`mailto:${d.emailSdi}`} className="text-[hsl(214,58%,41%)] hover:underline">{d.emailSdi}</a>)} />
        <Field label="Privato" value={fmtBool(d.privato)} />
        <Field label="Split payment" value={fmtBool(d.splitPayment)} />
        <Field label="Blocco cliente" value={fmtBool(d.bloccoCliente)} />
        <Field label="Data blocco" value={d.dataBlocco} />
        <Field label="Operatore blocco" value={d.operatoreBlocco} />
        <Field label="Data sblocco" value={d.dataSblocco} />
        <Field label="Operatore sblocco" value={d.operatoreSblocco} />
      </Section>

      {/* Indirizzo */}
      <Section icon={<MapPin className="w-4 h-4" />} title="Sede legale" testid="section-indirizzo">
        <Field label="Indirizzo" value={d.indirizzo} />
        <Field label="CAP" value={d.cap} mono />
        <Field label="Città" value={d.citta} />
        <Field label="Provincia" value={d.provincia} mono />
        <Field label="Nazione" value={d.nazione} mono />
      </Section>

      {/* Contatto aziendale */}
      <Section icon={<Phone className="w-4 h-4" />} title="Contatto aziendale" testid="section-contatti">
        <Field label="Email aziendale" value={d.email && (<a href={`mailto:${d.email}`} className="text-[hsl(214,58%,41%)] hover:underline">{d.email}</a>)} />
        <Field label="Telefono" value={d.telefono} mono />
        <Field label="Cellulare" value={d.cellulare} mono />
      </Section>

      {/* Riferimenti aziendali */}
      <Section icon={<Phone className="w-4 h-4" />} title="Riferimento Acquisti" testid="section-rif-acquisti">
        <Field label="Nominativo" value={d.rifAcquistiNominativo} />
        <Field label="Email" value={d.email && (<a href={`mailto:${d.email}`} className="text-[hsl(214,58%,41%)] hover:underline">{d.email}</a>)} />
        <Field label="Telefono" value={d.cellulare || d.telefono} mono />
      </Section>

      <Section icon={<Phone className="w-4 h-4" />} title="Riferimento Amministrativo" testid="section-rif-amministrativo">
        <Field label="Nominativo" value={d.rifAmministrativoNominativo} />
        <Field
          label="Email"
          value={d.rifAmministrativoEmail && (
            <a href={`mailto:${d.rifAmministrativoEmail}`} className="text-[hsl(214,58%,41%)] hover:underline">{d.rifAmministrativoEmail}</a>
          )}
        />
        <Field label="Telefono" value={d.rifAmministrativoTelefono} mono />
      </Section>

      <Section icon={<Phone className="w-4 h-4" />} title="Riferimento Tecnico" testid="section-rif-tecnico">
        <Field label="Nominativo" value={d.rifTecnicoNominativo} />
        <Field
          label="Email"
          value={d.rifTecnicoEmail && (
            <a href={`mailto:${d.rifTecnicoEmail}`} className="text-[hsl(214,58%,41%)] hover:underline">{d.rifTecnicoEmail}</a>
          )}
        />
        <Field label="Telefono" value={d.rifTecnicoTelefono} mono />
      </Section>

      {/* Logistica & spedizione */}
      <Section icon={<Truck className="w-4 h-4" />} title="Logistica & Spedizione" testid="section-logistica">
        <Field
          label="Vettore"
          value={
            d.vettoreLabel
              ? `${d.vettoreCodice ?? ''}${d.vettoreCodice != null ? ' - ' : ''}${d.vettoreLabel}`
              : (d.vettoreCodice != null ? `${d.vettoreCodice}` : null)
          }
        />
        <Field
          label="Trasporto minimo"
          value={d.trasportoMinimo != null && d.trasportoMinimo > 0
            ? `€ ${d.trasportoMinimo.toFixed(2).replace('.', ',')}`
            : null}
        />
        <Field
          label="Porto"
          value={
            d.portoLabel
              ? `${d.porto ?? ''}${d.porto ? ' - ' : ''}${d.portoLabel}`
              : (d.porto ? `${d.porto}` : null)
          }
        />
      </Section>

      {/* Agente esterno */}
      <Section icon={<Briefcase className="w-4 h-4" />} title="Agente esterno" testid="section-agente-esterno">
        <Field
          label="Nome"
          value={d.agenteEsternoNome || (d.agenteCodice != null ? `Codice ${d.agenteCodice}` : null)}
        />
        <Field
          label="Email"
          value={d.agenteEsternoEmail && (
            <a href={`mailto:${d.agenteEsternoEmail}`} className="text-[hsl(214,58%,41%)] hover:underline">{d.agenteEsternoEmail}</a>
          )}
        />
        <Field label="Telefono" value={d.agenteEsternoTelefono} mono />
      </Section>

      {/* Agente interno */}
      <Section icon={<Briefcase className="w-4 h-4" />} title="Agente interno" testid="section-agente-interno">
        <Field
          label="Nome"
          value={d.agenteInternoNome || (d.agenteInternoCodice != null ? `Codice ${d.agenteInternoCodice}` : null)}
        />
        <Field
          label="Email"
          value={d.agenteInternoEmail && (
            <a href={`mailto:${d.agenteInternoEmail}`} className="text-[hsl(214,58%,41%)] hover:underline">{d.agenteInternoEmail}</a>
          )}
        />
        <Field label="Telefono" value={d.agenteInternoTelefono} mono />
      </Section>

      {/* Condizioni commerciali */}
      <Section icon={<CreditCard className="w-4 h-4" />} title="Condizioni commerciali" testid="section-commerciali">
        <Field
          label="Listino"
          value={
            d.listinoLabel
              ? `${d.listino ?? ''}${d.listino != null ? ' - ' : ''}${d.listinoLabel}`
              : (d.listino != null ? `${d.listino}` : null)
          }
        />
        <Field
          label="Codice pagamento"
          value={
            d.codicePagamentoLabel
              ? `${d.codicePagamento ?? ''}${d.codicePagamento != null ? ' - ' : ''}${d.codicePagamentoLabel}`
              : (d.codicePagamento != null ? `${d.codicePagamento}` : null)
          }
        />
        <Field label="Sconto 1" value={d.sconto1 != null ? `${d.sconto1}%` : null} />
        <Field label="Sconto 2" value={d.sconto2 != null ? `${d.sconto2}%` : null} />
        <Field label="Banca" value={d.bancaDescrizione} />
        <Field label="Filiale" value={d.bancaFiliale} />
        <Field
          label="ABI / CAB"
          value={d.abi || d.cab ? <span className="font-mono">{d.abi ?? '—'} / {d.cab ?? '—'}</span> : null}
        />
        <Field
          label="IBAN"
          value={d.iban ? <span className="font-mono tracking-wide">{d.iban}</span> : null}
        />
      </Section>

      {/* Date */}
      <Section icon={<Calendar className="w-4 h-4" />} title="Date" testid="section-date">
        <Field label="Apertura cliente" value={fmtDate(d.dataApertura)} />
        <Field label="Inizio rapporto" value={fmtDate(d.dataInizioRapporto)} />
        <Field label="Fine rapporto" value={fmtDate(d.dataFineRapporto)} />
        <Field label="Ultimo aggiornamento NTS" value={fmtDate(d.dataUltimoAggiornamento)} />
      </Section>

      {/* Destinazioni */}
      {d.destinazioni && d.destinazioni.length > 0 && (
        <section className="rounded-xl border bg-white p-5" data-testid="section-destinazioni">
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-4 pb-3 border-b">
            <Truck className="w-4 h-4" />
            Destinazioni alternative
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-medium">
              {d.destinazioni.length}
            </span>
          </h2>
          <div className="space-y-3">
            {d.destinazioni.map((dd, idx) => {
              const isDefault = d.destinazionePreferita != null
                ? dd.dd_coddest === d.destinazionePreferita
                : idx === 0;
              return (
                <div key={`${dd.dd_coddest}-${idx}`} className="rounded-lg border p-3 bg-secondary/20" data-testid={`destination-${dd.dd_coddest ?? idx}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="text-sm font-semibold flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                        {dd.dd_nomdest || '(senza nome)'}
                        {isDefault && (
                          <span
                            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200"
                            data-testid={`destination-default-badge-${dd.dd_coddest}`}
                          >
                            Predefinita
                          </span>
                        )}
                      </div>
                      {dd.inddest && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {dd.inddest} — {dd.cap} {dd.citta} {dd.prov && `(${dd.prov})`}
                        </div>
                      )}
                    </div>
                    <span className="font-mono text-sm font-bold px-3 py-1 rounded-lg bg-[hsl(214,58%,41%)] text-white shrink-0" data-testid={`destination-code-badge-${dd.dd_coddest ?? idx}`}>
                      #{dd.dd_coddest}
                    </span>
                  </div>
                  {(dd.telef || dd.email) && (
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                      {dd.telef && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{dd.telef}</span>}
                      {dd.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{dd.email}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Account Web (richiesta utente 23/02/2026: Cambio Mail / Reset
          Credenziali / Dettaglio Accessi — valide solo per clienti con
          un'utenza sul portale; altrimenti card "Crea Utenza") */}
      <section className="rounded-xl border bg-white p-5" data-testid="section-account-web">
        <h2 className="text-sm font-semibold flex items-center gap-2 mb-4 pb-3 border-b">
          <ShieldCheck className="w-4 h-4" />
          Account Web
        </h2>
        {d.userId ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="detail-account-titolare">
              <span>
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mr-1.5">Titolare</span>
                <span className="font-medium">{[d.accountNome, d.accountCognome].filter(Boolean).join(' ') || '—'}</span>
              </span>
              <span className="text-muted-foreground">{d.accountEmail || '—'}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${d.accountAttivo ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {d.accountAttivo ? 'Attivo' : 'Non attivo'}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ChangeEmailCard clientKey={anConto} currentEmail={d.accountEmail} onSuccess={() => load(false)} />
              <ResetCredentialsCard clientKey={anConto} email={d.accountEmail} onSuccess={() => load(false)} />
            </div>
            <Link
              to={`/admin/clienti/${anConto}/accessi`}
              className="inline-flex items-center gap-1.5 text-sm text-[hsl(214,58%,41%)] hover:underline font-medium"
              data-testid="detail-link-accessi"
            >
              <ShieldCheck className="w-4 h-4" /> Vedi Dettaglio Accessi →
            </Link>
          </div>
        ) : (
          <div className="max-w-sm">
            <CreateAccountCard anConto={anConto} defaultEmail={d.email} onSuccess={() => load(false)} />
          </div>
        )}
      </section>
    </div>
  );
}


/** Route ``/admin/clienti/:anConto`` — la scheda ERP completa del Cliente. */
export default function AdminClientDetailPage() {
  const { anConto: param } = useParams<{ anConto: string }>();
  return <ErpClientDetail anConto={Number(param)} />;
}
